import { useState, useEffect } from "react";
import { LanguageProvider } from "./lib/language";
import { QueryProvider } from "./lib/QueryProvider";
import { Header } from "./components/Header";
import { Hero } from "./components/Hero";
import { AboutSection } from "./components/AboutSection";
import { LeadContactForm } from "./components/LeadContactForm";
import { AgentProfile } from "./components/AgentProfile";
import { Footer } from "./components/Footer";
import { FloatingWhatsApp } from "./components/FloatingWhatsApp";
import { CookieConsentBanner } from "./components/CookieConsent";
import { NewProjectsBanner } from "./components/NewProjectsBanner";
import { AdminPanel } from "./pages/admin";
import { PrivacyPolicyPage } from "./pages/PrivacyPolicy";
import { TermsOfUsePage } from "./pages/TermsOfUse";
import { AccessibilityStatementPage } from "./pages/AccessibilityStatement";
import { Language } from "./lib/types";
import { Toaster } from "@/components/ui/sonner";
import { trackPageView } from "./lib/analytics";

// Enhanced router hook with language support
function useRouter() {
  const [pathname, setPathname] = useState(window.location.pathname);

  useEffect(() => {
    const handlePopState = () => {
      setPathname(window.location.pathname);
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  const navigate = (path: string) => {
    window.history.pushState({}, "", path);
    setPathname(path);
  };

  // Extract language from pathname
  const getLanguageFromPath = (): Language | null => {
    const pathSegments = pathname.split("/").filter(Boolean);
    const firstSegment = pathSegments[0];

    if (
      firstSegment === "he" ||
      firstSegment === "en" ||
      firstSegment === "fr"
    ) {
      return firstSegment as Language;
    }

    return null;
  };

  // Get the pathname without the language prefix
  const getPathWithoutLanguage = (): string => {
    const pathSegments = pathname.split("/").filter(Boolean);
    const firstSegment = pathSegments[0];

    if (
      firstSegment === "he" ||
      firstSegment === "en" ||
      firstSegment === "fr"
    ) {
      return "/" + pathSegments.slice(1).join("/");
    }

    return pathname;
  };

  return {
    pathname,
    navigate,
    getLanguageFromPath,
    getPathWithoutLanguage,
  };
}

function AppContent() {
  const { pathname, navigate, getLanguageFromPath, getPathWithoutLanguage } =
    useRouter();

  // All hooks must be called at the top level, before any early returns

  // Track page views for Google Analytics
  useEffect(() => {
    trackPageView(pathname);
  }, [pathname]);

  // Handle language routing and redirects
  useEffect(() => {
    const currentLang = getLanguageFromPath();
    const pathWithoutLang = getPathWithoutLanguage();

    // If no language in URL, redirect to default language (English)
    if (!currentLang && pathname === "/") {
      navigate("/en");
      return;
    }

    // If no language in URL but there's a path, redirect to English version
    if (!currentLang && pathname !== "/") {
      navigate("/en" + pathname);
      return;
    }
  }, [pathname, navigate, getLanguageFromPath, getPathWithoutLanguage]);

  // Handle section scrolling when navigating back to home with hash
  useEffect(() => {
    const hash = window.location.hash;
    if (
      hash &&
      (getPathWithoutLanguage() === "/" || getPathWithoutLanguage() === "")
    ) {
      // Remove the # from hash
      const sectionId = hash.substring(1);
      // Wait a bit for the page to load, then scroll
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: "smooth" });
        }
      }, 100);
    }
  }, [pathname, getPathWithoutLanguage]);

  // Now we can safely use conditional rendering
  const currentLanguage = getLanguageFromPath();
  const pathWithoutLanguage = getPathWithoutLanguage();

  // If the path is /admin (with any language prefix), show the admin panel
  if (pathWithoutLanguage.startsWith("/admin")) {
    return (
      <LanguageProvider initialLanguage={currentLanguage || "en"}>
        <AdminPanel />
      </LanguageProvider>
    );
  }

  // If the path is /privacy-policy, show the privacy policy page
  if (pathWithoutLanguage === "/privacy-policy") {
    return (
      <LanguageProvider initialLanguage={currentLanguage || "en"}>
        <div
          className="min-h-screen bg-background"
          key={`privacy-${currentLanguage}`}
        >
          <Header />
          <main id="main-content">
            <PrivacyPolicyPage />
          </main>
          <Footer />
        </div>
      </LanguageProvider>
    );
  }

  // If the path is /terms-of-use, show the terms of use page
  if (pathWithoutLanguage === "/terms-of-use") {
    return (
      <LanguageProvider initialLanguage={currentLanguage || "en"}>
        <div
          className="min-h-screen bg-background"
          key={`terms-${currentLanguage}`}
        >
          <Header />
          <main id="main-content">
            <TermsOfUsePage />
          </main>
          <Footer />
        </div>
      </LanguageProvider>
    );
  }

  // If the path is /accessibility-statement, show the accessibility statement page
  if (pathWithoutLanguage === "/accessibility-statement") {
    return (
      <LanguageProvider initialLanguage={currentLanguage || "en"}>
        <div
          className="min-h-screen bg-background"
          key={`accessibility-${currentLanguage}`}
        >
          <Header />
          <main id="main-content">
            <AccessibilityStatementPage />
          </main>
          <Footer />
        </div>
      </LanguageProvider>
    );
  }

  // Don't render main content until we have a proper language path
  if (!currentLanguage) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <LanguageProvider initialLanguage={currentLanguage}>
      <div className="min-h-screen bg-background">
        <a
          href="#main-content"
          className="sr-only focus:not-sr-only focus:fixed focus:top-4 focus:left-4 focus:z-[9999] focus:bg-primary focus:text-primary-foreground focus:px-4 focus:py-2 focus:rounded-md focus:font-medium"
        >
          Skip to main content
        </a>
        <Header />
        <main id="main-content">
          <Hero />
          <AboutSection />
          <NewProjectsBanner />
          <AgentProfile />
          <LeadContactForm />
          {/* <PropertiesSection /> */}
        </main>
        <Footer />
        <FloatingWhatsApp />
        <CookieConsentBanner />
        <Toaster />
      </div>
    </LanguageProvider>
  );
}

function App() {
  return (
    <QueryProvider>
      <AppContent />
    </QueryProvider>
  );
}

export default App;
